import React, { useState } from "react";

// ============================
// Design Tokens (from spec)
// ============================
const COLORS = {
  primaryBlue: "#1A4F8A",
  primaryDark: "#0D3366",
  successGreen: "#2E7D32",
  warningAmber: "#F57C00",
  dangerRed: "#C62828",
  backgroundGray: "#F5F7FA",
  mutedText: "#546E7A",
  border: "#B0C4DE",
  white: "#FFFFFF",
};

// ============================
// Mock Initial Case Data
// (each entry has an id and a real date)
// ============================
const INITIAL_CASES = [
  {
    id: 1,
    date: "2024-04-01",
    cases: 32,
    mg: 30,
    billable: 32,
    mgApplied: false,
    editHistory: [],
  },
  {
    id: 2,
    date: "2024-04-02",
    cases: 18,
    mg: 30,
    billable: 30,
    mgApplied: true,
    editHistory: [{ timestamp: "2024-04-03 10:30", oldCases: 18, newCases: 18, reason: "Initial entry" }],
  },
  {
    id: 3,
    date: "2024-04-03",
    cases: 35,
    mg: 30,
    billable: 35,
    mgApplied: false,
    editHistory: [],
  },
  {
    id: 4,
    date: "2024-04-04",
    cases: 22,
    mg: 30,
    billable: 30,
    mgApplied: true,
    editHistory: [],
  },
];

// Helper: format YYYY-MM-DD to "MMM DD"
const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
};

// ============================
// Reusable UI Components
// ============================
const Button = ({ children, onClick, variant = "primary", disabled = false, icon }) => {
  const variants = {
    primary: { background: COLORS.primaryBlue, color: COLORS.white, border: "none", hoverBg: COLORS.primaryDark },
    secondary: { background: "transparent", color: COLORS.primaryBlue, border: `1px solid ${COLORS.primaryBlue}`, hoverBg: "#E3EDF7" },
    danger: { background: COLORS.dangerRed, color: COLORS.white, border: "none", hoverBg: "#B71C1C" },
  };
  const style = variants[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...style,
        padding: "8px 16px",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.2s",
        height: "36px",
      }}
      onMouseEnter={(e) => !disabled && (e.currentTarget.style.background = style.hoverBg)}
      onMouseLeave={(e) => !disabled && (e.currentTarget.style.background = style.background)}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
};

const Input = ({ label, error, ...props }) => (
  <div style={{ marginBottom: "16px" }}>
    {label && (
      <label style={{ fontSize: "12px", fontWeight: 500, marginBottom: "4px", display: "block", color: COLORS.mutedText }}>
        {label} {props.required && <span style={{ color: COLORS.dangerRed }}>*</span>}
      </label>
    )}
    <input
      {...props}
      style={{
        border: `1px solid ${error ? COLORS.dangerRed : COLORS.border}`,
        borderRadius: "6px",
        height: "40px",
        padding: "0 12px",
        fontSize: "14px",
        width: "100%",
        outline: "none",
        ...props.style,
      }}
      onFocus={(e) => (e.target.style.borderColor = COLORS.primaryBlue)}
      onBlur={(e) => (e.target.style.borderColor = error ? COLORS.dangerRed : COLORS.border)}
    />
    {error && <div style={{ color: COLORS.dangerRed, fontSize: "12px", marginTop: "4px" }}>{error}</div>}
  </div>
);

const Select = ({ value, onChange, options, label }) => (
  <div style={{ marginBottom: "16px" }}>
    {label && <label style={{ fontSize: "12px", fontWeight: 500, marginBottom: "4px", display: "block", color: COLORS.mutedText }}>{label}</label>}
    <select
      value={value}
      onChange={onChange}
      style={{
        height: "40px",
        padding: "0 12px",
        border: `1px solid ${COLORS.border}`,
        borderRadius: "6px",
        background: COLORS.white,
        width: "100%",
      }}
    >
      {options.map((opt) => (
        <option key={opt.value || opt} value={opt.value || opt}>
          {opt.label || opt}
        </option>
      ))}
    </select>
  </div>
);

const Modal = ({ isOpen, onClose, title, children, footer }) => {
  if (!isOpen) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: COLORS.white,
          width: "90%",
          maxWidth: "560px",
          borderRadius: "12px",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontSize: "18px", fontWeight: 600, margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: "24px" }}>{children}</div>
        {footer && (
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

const Toast = ({ message, type, visible }) => {
  const variants = {
    success: { bg: "#E8F5E9", color: COLORS.successGreen, border: "#A5D6A7", icon: "✓" },
    warning: { bg: "#FFF3E0", color: COLORS.warningAmber, border: "#FFE0B2", icon: "⚠" },
    error: { bg: "#FFEBEE", color: COLORS.dangerRed, border: "#FFCDD2", icon: "✕" },
    info: { bg: "#E3F2FD", color: COLORS.primaryBlue, border: "#90CAF9", icon: "ℹ" },
  };
  const style = variants[type] || variants.info;
  if (!visible) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: "80px",
        right: "24px",
        zIndex: 9999,
        background: style.bg,
        color: style.color,
        borderLeft: `4px solid ${style.color}`,
        borderRadius: "8px",
        padding: "12px 20px",
        fontSize: "14px",
        fontWeight: 500,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        animation: "slideIn 0.2s ease-out",
      }}
    >
      <span style={{ fontWeight: 700 }}>{style.icon}</span>
      {message}
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ label, value, borderColor }) => (
  <div
    style={{
      background: COLORS.white,
      border: `1px solid ${COLORS.border}`,
      borderLeft: `4px solid ${borderColor}`,
      padding: "18px",
      borderRadius: "10px",
    }}
  >
    <div style={{ fontSize: "30px", fontWeight: 700, marginBottom: "8px" }}>{value}</div>
    <div style={{ fontSize: "13px", color: COLORS.mutedText }}>{label}</div>
  </div>
);

// ============================
// Main CaseEntry Component
// ============================
export default function CaseEntry({ camp = { name: "Camp Alpha", rate: 250, mgValue: 30 } }) {
  const [cases, setCases] = useState(INITIAL_CASES);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: "", type: "info" });

  // Form state for Add/Edit
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    casesCount: "",
    reason: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const showToastMsg = (message, type = "info") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 4000);
  };

  // Filter cases by date range
  const filteredCases = cases.filter((entry) => {
    if (!startDate && !endDate) return true;
    const entryDate = entry.date;
    if (startDate && entryDate < startDate) return false;
    if (endDate && entryDate > endDate) return false;
    return true;
  });

  // Sort by date descending (newest first)
  const sortedCases = [...filteredCases].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Calculate summary stats
  const totalCases = sortedCases.reduce((sum, e) => sum + e.cases, 0);
  const totalBillable = sortedCases.reduce((sum, e) => sum + e.billable, 0);
  const mgTriggerDays = sortedCases.filter((e) => e.mgApplied).length;
  const projectedBilling = sortedCases.reduce((sum, e) => sum + e.billable * camp.rate, 0);

  // Helper: compute billable and mgApplied from raw cases and camp mgValue
  const computeBillable = (rawCases) => {
    const billable = Math.max(rawCases, camp.mgValue);
    const mgApplied = rawCases < camp.mgValue;
    return { billable, mgApplied };
  };

  // Add new case entry
  const handleAddCase = () => {
    const errors = {};
    if (!form.date) errors.date = "Date is required";
    if (!form.casesCount || form.casesCount < 0) errors.casesCount = "Valid case count is required";
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    const raw = Number(form.casesCount);
    const { billable, mgApplied } = computeBillable(raw);
    const newEntry = {
      id: Date.now(),
      date: form.date,
      cases: raw,
      mg: camp.mgValue,
      billable,
      mgApplied,
      editHistory: [{ timestamp: new Date().toISOString(), oldCases: null, newCases: raw, reason: form.reason || "Initial entry" }],
    };
    setCases([newEntry, ...cases]);
    showToastMsg("Case entry added successfully", "success");
    setShowAddModal(false);
    resetForm();
  };

  // Edit existing case entry
  const handleEditCase = () => {
    const errors = {};
    if (!form.casesCount || form.casesCount < 0) errors.casesCount = "Valid case count is required";
    if (!form.reason || form.reason.trim().length < 10) errors.reason = "Reason for edit is required (min 10 characters)";
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    const raw = Number(form.casesCount);
    const { billable, mgApplied } = computeBillable(raw);
    const updatedCases = cases.map((entry) => {
      if (entry.id === selectedEntry.id) {
        const newHistory = [
          ...(entry.editHistory || []),
          { timestamp: new Date().toISOString(), oldCases: entry.cases, newCases: raw, reason: form.reason },
        ];
        return {
          ...entry,
          cases: raw,
          billable,
          mgApplied,
          editHistory: newHistory,
        };
      }
      return entry;
    });
    setCases(updatedCases);
    showToastMsg("Case entry updated successfully", "success");
    setShowEditModal(false);
    resetForm();
  };

  const resetForm = () => {
    setForm({ date: new Date().toISOString().split("T")[0], casesCount: "", reason: "" });
    setFormErrors({});
    setSelectedEntry(null);
  };

  const openEditModal = (entry) => {
    setSelectedEntry(entry);
    setForm({
      date: entry.date,
      casesCount: entry.cases.toString(),
      reason: "",
    });
    setShowEditModal(true);
  };

  const openHistoryModal = (entry) => {
    setSelectedEntry(entry);
    setShowHistoryModal(true);
  };

  // Billing preview (shared for both modals)
  const previewBillable = form.casesCount ? Math.max(Number(form.casesCount), camp.mgValue) : 0;
  const previewAmount = previewBillable * camp.rate;
  const mgTriggerPreview = form.casesCount && Number(form.casesCount) < camp.mgValue;

  return (
    <div style={{ width: "100%" }}>
      <Toast visible={toast.visible} message={toast.message} type={toast.type} />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: 700, margin: "0 0 4px 0", color: "#1A2C3E" }}>Case Entry</h1>
          <div style={{ fontSize: "13px", color: COLORS.mutedText }}>{camp.name} – Daily Cases Management</div>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {/* Date Range Filter */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ height: "36px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}` }}
              placeholder="Start"
            />
            <span>–</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ height: "36px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}` }}
              placeholder="End"
            />
            <Button variant="secondary" onClick={() => { setStartDate(""); setEndDate(""); }}>Clear</Button>
          </div>
          <Button icon="+" onClick={() => setShowAddModal(true)}>Add Cases for Today</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <SummaryCard label="Total Cases Entered" value={totalCases} borderColor={COLORS.primaryBlue} />
        <SummaryCard label="Billable Cases" value={totalBillable} borderColor={COLORS.successGreen} />
        <SummaryCard label="MG Triggered Days" value={mgTriggerDays} borderColor={COLORS.warningAmber} />
        <SummaryCard label="Projected Billing" value={`₹${projectedBilling.toLocaleString()}`} borderColor={COLORS.primaryBlue} />
      </div>

      {/* Cases Table */}
      <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: "10px", background: COLORS.white }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead style={{ background: COLORS.backgroundGray }}>
            <tr>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Date</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Cases Entered</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>MG</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Billable Cases</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Edit History</th>
            </tr>
          </thead>
          <tbody>
            {sortedCases.map((entry) => (
              <tr
                key={entry.id}
                style={{
                  borderBottom: `1px solid ${COLORS.border}`,
                  borderLeft: entry.mgApplied ? `3px solid ${COLORS.warningAmber}` : "3px solid transparent",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFE")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={{ padding: "12px 16px" }}>{formatDate(entry.date)}</td>
                <td style={{ padding: "12px 16px", fontWeight: 600 }}>{entry.cases}</td>
                <td style={{ padding: "12px 16px" }}>{entry.mg}</td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{ fontWeight: 700, color: entry.mgApplied ? COLORS.warningAmber : "inherit" }}>
                    {entry.billable}
                    {entry.mgApplied && " (MG)"}
                  </span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <button
                      onClick={() => openEditModal(entry)}
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px" }}
                      title="Edit entry"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => openHistoryModal(entry)}
                      style={{ background: "none", border: "none", color: COLORS.primaryBlue, fontWeight: 600, cursor: "pointer" }}
                    >
                      View Edits
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedCases.length === 0 && (
          <div style={{ padding: "48px", textAlign: "center", color: COLORS.mutedText }}>
            No case entries found for the selected date range.
          </div>
        )}
      </div>

      {/* ADD MODAL */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); resetForm(); }}
        title="Add Daily Case Entry"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setShowAddModal(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleAddCase}>Save Cases</Button>
          </>
        }
      >
        <Input
          label="Date"
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          required
          error={formErrors.date}
        />
        <Input
          label="Cases Count"
          type="number"
          value={form.casesCount}
          onChange={(e) => setForm({ ...form, casesCount: e.target.value })}
          required
          error={formErrors.casesCount}
          placeholder="Enter number of cases"
        />
        <Input
          label="Reason for Edit (optional)"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          placeholder="Optional reason for this entry"
        />
        {/* Billing Preview */}
        {form.casesCount && (
          <div style={{ background: "#EFF4FC", padding: "16px", borderRadius: "8px", marginTop: "16px" }}>
            <div style={{ fontWeight: 700, color: COLORS.primaryBlue, marginBottom: "8px" }}>Billing Preview</div>
            <div>Raw Cases: <strong>{form.casesCount}</strong></div>
            <div>Billable Cases: <strong>{previewBillable}</strong></div>
            <div>Amount: <strong>₹{previewAmount.toLocaleString()}</strong></div>
            {mgTriggerPreview && (
              <div style={{ marginTop: "8px", color: COLORS.warningAmber, fontWeight: 700 }}>⚠️ MG Trigger Applied</div>
            )}
          </div>
        )}
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); resetForm(); }}
        title="Edit Case Entry"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleEditCase}>Update Cases</Button>
          </>
        }
      >
        <Input
          label="Date"
          type="date"
          value={form.date}
          disabled
          style={{ background: COLORS.backgroundGray, cursor: "not-allowed" }}
        />
        <Input
          label="Cases Count"
          type="number"
          value={form.casesCount}
          onChange={(e) => setForm({ ...form, casesCount: e.target.value })}
          required
          error={formErrors.casesCount}
        />
        <Input
          label="Reason for Edit *"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          required
          error={formErrors.reason}
          placeholder="Explain why you are editing (min 10 characters)"
        />
        {form.casesCount && (
          <div style={{ background: "#EFF4FC", padding: "16px", borderRadius: "8px", marginTop: "16px" }}>
            <div style={{ fontWeight: 700, color: COLORS.primaryBlue, marginBottom: "8px" }}>Billing Preview</div>
            <div>Raw Cases: <strong>{form.casesCount}</strong></div>
            <div>Billable Cases: <strong>{previewBillable}</strong></div>
            <div>Amount: <strong>₹{previewAmount.toLocaleString()}</strong></div>
            {mgTriggerPreview && (
              <div style={{ marginTop: "8px", color: COLORS.warningAmber, fontWeight: 700 }}>⚠️ MG Trigger Applied</div>
            )}
          </div>
        )}
      </Modal>

      {/* EDIT HISTORY MODAL */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title={`Edit History – ${selectedEntry ? formatDate(selectedEntry.date) : ""}`}
        footer={<Button variant="secondary" onClick={() => setShowHistoryModal(false)}>Close</Button>}
      >
        {selectedEntry && selectedEntry.editHistory && selectedEntry.editHistory.length > 0 ? (
          <div>
            {selectedEntry.editHistory.map((record, idx) => (
              <div key={idx} style={{ borderBottom: `1px solid ${COLORS.border}`, padding: "12px 0" }}>
                <div style={{ fontSize: "12px", color: COLORS.mutedText }}>{new Date(record.timestamp).toLocaleString()}</div>
                <div>Changed from <strong>{record.oldCases ?? "—"}</strong> → <strong>{record.newCases}</strong></div>
                <div>Reason: {record.reason}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", color: COLORS.mutedText, padding: "24px" }}>No edit history available.</div>
        )}
      </Modal>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}